import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useApiClient } from '@core/api/useApiClient';
import { blockCqgAutoResume, setNextLogonDropConcurrent } from '../lib/cqgRelay';
import { getRelayOwnership } from '@core/realtime/relayOwnership';
import { readStoredCqgCreds } from '../lib/cqgCreds';

interface ConnectRequest {
  // Take over the user's OTHER active CQG session (their trading platform) — arms the one-shot
  // drop_concurrent_session flag on the NEXT FE-built Logon. Only set by the explicit "Take over"
  // action; silent reconnects leave it off so a conflict prompts instead of kicking.
  takeOver?: boolean;
}

/**
 * CREDENTIAL-FREE connect (2026-07-08: CQG creds are forbidden from transiting our BE). The POST
 * carries ONLY the chosen `environment` ('demo' | 'live' — non-secret, it just picks the gateway +
 * app-id pair, mirroring Rithmic's `system`); the BE resolves the pair and pushes CqgOpenSocket back;
 * the relay then builds + sends the Logon itself from localStorage creds (store them via
 * storeCqgCreds BEFORE calling this). The environment is read from the SAME stored creds the relay
 * uses, so the dialed gateway and the user's choice always agree.
 */
/**
 * ONE CQG connect at a time (2026-08-07 user request: "only one connection at a time").
 *
 * Module-level rather than per-hook on purpose: `useCqgConnect()` is called from three places
 * (AddAccountDialog, CqgConnectorDialog, CqgReconnectPopup), each getting its own mutation, so
 * React Query's per-instance `isPending` cannot see a connect started by a different component.
 * There is only ever one CQG socket, so a second connect racing the first can only fight it — the
 * later Logon wins the session while the earlier one's UI still believes it is connecting.
 */
let connectInFlight = false;

/** Test seam — the module flag would otherwise leak between cases. */
export function resetCqgConnectGuard(): void {
  connectInFlight = false;
}

export class CqgConnectInFlightError extends Error {
  constructor() {
    super('A CQG connection is already being established.');
    this.name = 'CqgConnectInFlightError';
  }
}

export function useCqgConnect() {
  const api = useApiClient();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ takeOver }: ConnectRequest = {}) => {
      if (connectInFlight) throw new CqgConnectInFlightError();
      connectInFlight = true;
      try {
        if (takeOver) setNextLogonDropConcurrent();
        return await api.post<void>('/api/v1/cqg/connect', {
          environment: readStoredCqgCreds().environment,
        });
      } finally {
        // Cleared on failure too: a connect that never completes must not wedge the guard shut and
        // leave the user unable to retry.
        connectInFlight = false;
      }
    },
    // ['me'] too: the BE sets the `cqg_connected` action flag on the first successful logon, and the
    // first-time banner gates on it — without this, only the connected-popup's narrow live-push path
    // refreshed /me and a stale flag kept the "Start here" bar visible for already-synced users
    // (2026-07-08 bug). Purely a cache refresh — no CQG session behavior change.
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['cqg', 'status'] });
      void qc.invalidateQueries({ queryKey: ['me'] });
    },
  });
}

/**
 * Explicit concurrent-login request (Account Sync Workflow). Asks Optimus support to set up a
 * concurrent login with the FCM. Called at the explicit connect-flow moments — a user-initiated
 * connect going live, and the take-over "Cancel" (keep-both) path. Idempotent + deduped server-side,
 * so calling it more than once is safe. Invalidates `me` so the FE sees `concurrent_request_submitted`.
 */
export function useRequestConcurrentLogin() {
  const api = useApiClient();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => api.post<void>('/api/v1/cqg/concurrent-login-request'),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['me'] }),
  });
}

export function useCqgDisconnect() {
  const api = useApiClient();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => {
      // The user's own Disconnect is terminal for auto-resume on every tab and across reloads (D4,
      // 2026-09-13) — latched BEFORE the POST so the state push it triggers cannot race a resume.
      // Only an explicit Connect / Take-over (runCqgConnectSequence) releases it. Both Disconnect
      // surfaces (the dialog and the Accounts panel's Unlink) come through this mutation.
      blockCqgAutoResume('user-disconnected');
      getRelayOwnership('cqg').release(); // D1: the other tabs learn the owner let go
      return api.post<void>('/api/v1/cqg/disconnect');
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['cqg', 'status'] }),
  });
}

/**
 * Subscribe live market data for a set of symbols. The BE resolves each contract and starts
 * streaming `quotes.update` for it (it auto-subscribes traded symbols on import too, but this
 * lets a view explicitly request quotes for whatever symbols it's displaying). No-op server-side
 * if the CQG session isn't live yet.
 */
export function useCqgSubscribe() {
  const api = useApiClient();
  return useMutation({
    mutationFn: (symbols: string[]) => api.post<void>('/api/v1/cqg/subscribe', { symbols }),
  });
}

export function useCqgImport() {
  const api = useApiClient();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: { from?: string; to?: string; account?: string }) =>
      api.post<{ jobId: string }>('/api/v1/cqg/import', body),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['trades'] });
      qc.invalidateQueries({ queryKey: ['statistics'] });
    },
  });
}
